import React            from 'react';
import { EditOutlined } from '@ant-design/icons';
import Input            from '@widesk-ui/components/form/Input';
import View             from '@widesk-ui/components/View';
import { Props }        from '@widesk-ui/components/View/viewTypes';
import useTheme         from '@widesk-ui/hooks/useTheme';

export type EditableTextProps = {
	value?: string;
	/**
	 * Appelé au seul cas où la valeur a réellement changé. Lever une `Error` laisse le champ ouvert et
	 * affiche son `message` sous l'input — l'utilisateur peut corriger sans avoir perdu sa saisie.
	 */
	onSubmit: (value: string) => void | Promise<void>;
	/** Affiché à la place du texte quand la valeur est vide : sans lui, il n'y aurait rien à cliquer. */
	emptyLabel?: React.ReactNode;
	/** Message de repli si `onSubmit` échoue sans lever d'`Error`. */
	errorMessage?: string;
	placeholder?: string;
	disabled?: boolean;
	size?: ComponentSize;
	maxLength?: number;
	/** Style du texte en mode affichage — pour reprendre la typographie de l'écran hôte. */
	textProps?: Props;
	/** Remplace le fond de survol par défaut. `{}` pour n'en avoir aucun. */
	hoverProps?: Props;
	/** Ajouté au style de l'input, après l'héritage typographique — pour le cas où hériter ne convient
	 * pas (ex. un texte affiché en très grande taille qu'on ne veut pas saisir tel quel). */
	inputStyle?: React.CSSProperties;
	/** `'text'` (défaut) : tout le texte ouvre la saisie. `'icon'` : seul le crayon l'ouvre, le texte
	 * laisse passer ses clics à l'élément parent. */
	editTrigger?: 'text' | 'icon';
};

/**
 * Texte modifiable en place : il s'affiche comme du texte normal, se signale au survol, et devient un
 * champ de saisie au clic.
 *
 * Choix de comportement, tous délibérés :
 *  - **Entrée** valide, **Échap** annule (la saisie est jetée), **perte de focus** valide — c'est la
 *    convention de l'édition en place, où l'on attend que cliquer ailleurs conserve ce qu'on vient
 *    d'écrire. Pour une validation explicite par bouton, voir `InputOk`.
 *  - Une valeur inchangée ne déclenche PAS `onSubmit` : ouvrir puis refermer le champ sans rien
 *    modifier ne doit pas provoquer d'enregistrement (ni de requête chez l'appelant).
 *  - Le texte est sélectionné à l'ouverture : le cas courant est de remplacer, pas de compléter.
 *  - L'input HÉRITE de la typographie environnante (police, taille, graisse, couleur) : le champ doit
 *    ressembler au texte qu'il remplace, sinon le clic provoque un saut visuel. L'appelant n'a donc
 *    rien à redéclarer — voir `inputStyle` pour les cas où hériter ne convient pas.
 */
export default function EditableText(props: EditableTextProps) {
	const theme = useTheme();
	const iconOnly = props.editTrigger === 'icon';

	const [editing, setEditing] = React.useState(false);
	const [draft, setDraft] = React.useState(props.value || '');
	const [error, setError] = React.useState<string>();
	const [loading, setLoading] = React.useState(false);

	// `Échap` referme le champ, ce qui déclenche aussi son `blur` : sans ce drapeau, l'annulation
	// serait immédiatement suivie d'une validation.
	const cancelling = React.useRef(false);

	// La valeur peut changer depuis l'extérieur (autre écran, annulation, rechargement) : le brouillon
	// doit suivre tant qu'on n'est pas en train de le modifier.
	React.useEffect(() => {
		if (!editing) setDraft(props.value || '');
	}, [props.value, editing]);

	const submit = async () => {
		const next = draft.trim();

		if (next === (props.value || '').trim()) {
			setEditing(false);
			setError(undefined);
			return;
		}

		setLoading(true);

		try {
			await props.onSubmit(next);
			setEditing(false);
			setError(undefined);
		} catch (err) {
			setError(err instanceof Error ? err.message : (props.errorMessage || 'Modification impossible'));
		} finally {
			setLoading(false);
		}
	};

	const cancel = () => {
		cancelling.current = true;
		setDraft(props.value || '');
		setError(undefined);
		setEditing(false);
	};

	if (editing) {
		return (
			<View gap={4} onClick={iconOnly ? e => e.stopPropagation() : undefined}>
				<Input
					autoFocus
					value={draft}
					disabled={loading}
					size={props.size}
					maxLength={props.maxLength}
					placeholder={props.placeholder}
					style={{
						fontFamily: 'inherit',
						fontSize: 'inherit',
						fontWeight: 'inherit',
						color: 'inherit',

						...props.inputStyle,
					}}
					// `Input` déclare `onChange?: (value: string) => void` mais passe le handler tel quel à
					// antd (voir `Input.tsx`), qui l'appelle avec l'ÉVÉNEMENT. On lit donc `target.value`,
					// comme le fait déjà `InputOk`.
					onChange={(e: any) => setDraft(e?.target?.value ?? '')}
					onFocus={e => e.target.select()}
					onPressEnter={submit}
					onKeyDown={e => {
						if (e.key === 'Escape') cancel();
					}}
					onBlur={() => {
						if (cancelling.current) {
							cancelling.current = false;
							return;
						}

						void submit();
					}}
				/>

				{!!error && (
					<View color={theme.colorError} size={theme.sizeSM}>
						{error}
					</View>
				)}
			</View>
		);
	}

	const empty = !props.value;
	const hover = props.disabled ? {} : (props.hoverProps || { bg: theme.colorFillTertiary });
	const startEditing = () => setEditing(true);

	const icon = <EditOutlined style={{ color: theme.colorTextDescription, fontSize: theme.sizeSM, flexShrink: 0 }} />;

	return (
		<View
			row
			gap="xs"
			centerV
			br={4}
			px={4}
			pointer={!props.disabled && !iconOnly}
			style={{ marginLeft: -4, minWidth: 0 }}
			hover={iconOnly ? {} : hover}
			onClick={props.disabled || iconOnly ? undefined : startEditing}

			{...props.textProps}
		>
			{/* `display: block` explicite : `View` rend toujours un conteneur flex, où
			    `text-overflow: ellipsis` n'a aucun effet — le texte était coupé net, sans les points. */}
			<View
				style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
				color={empty ? theme.colorTextDescription : undefined}
			>
				{empty ? props.emptyLabel : props.value}
			</View>

			{!props.disabled && (iconOnly ? (
				<View
					br={4}
					p={2}
					pointer
					hover={hover}
					style={{ flexShrink: 0 }}
					onClick={e => {
						e.stopPropagation();
						startEditing();
					}}
				>
					{icon}
				</View>
			) : icon)}
		</View>
	);
}
